import memberstack from './memberstack_client';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'memberstack';

const ALLOWED_PLAN_IDS = new Set([
  'pln_vip-membership-c0ea0lhm',
  'pln_14-day-free-trial-62177048g',
  'pln_spx-gamma-7-day-free-trial-ny3f01hl',
  'pln_legacy-membership-337d0n3j',
  'pln_premium-seller-membership-q2en0as4',
  'pln_7-day-membership-zd1d088k',
]);

const ALLOWED_STATUSES = new Set(['active', 'trialing']);

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

const LOCAL_AUTH = {
  type: 'AUTH',
  memberId: process.env.REACT_APP_LOCAL_MEMBER_ID,
  planId: process.env.REACT_APP_LOCAL_PLAN_ID,
  status: normalizeStatus(process.env.REACT_APP_LOCAL_PLAN_STATUS),
};

function validateLocalAuth() {
  if (!LOCAL_AUTH.memberId || !LOCAL_AUTH.planId || !LOCAL_AUTH.status) {
    throw new Error(
      'Manual auth mode is enabled, but REACT_APP_LOCAL_MEMBER_ID, REACT_APP_LOCAL_PLAN_ID, or REACT_APP_LOCAL_PLAN_STATUS is missing.'
    );
  }

  return LOCAL_AUTH;
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitForMemberstackGlobals(maxWaitMs = 5000) {
  const startedAt = Date.now();

  while (Date.now() - startedAt < maxWaitMs) {
    if (
      typeof window !== 'undefined' &&
      (window.MemberStack || window.$memberstackDom || window.memberstack)
    ) {
      return true;
    }

    await sleep(100);
  }

  return false;
}

function unwrapMemberstackResponse(response) {
  if (!response) return null;

  return (
    response?.data?.member ||
    response?.data?.currentMember ||
    response?.data?.memberData ||
    response?.data ||
    response?.member ||
    response?.currentMember ||
    response?.memberData ||
    response?.user ||
    response
  );
}

function getDebugShape(value) {
  if (!value || typeof value !== 'object') return value;

  return {
    topLevelKeys: Object.keys(value),
    dataKeys:
      value?.data && typeof value.data === 'object'
        ? Object.keys(value.data)
        : null,
    memberKeys:
      value?.member && typeof value.member === 'object'
        ? Object.keys(value.member)
        : null,
    currentMemberKeys:
      value?.currentMember && typeof value.currentMember === 'object'
        ? Object.keys(value.currentMember)
        : null,
    planConnectionsCount: Array.isArray(value?.planConnections)
      ? value.planConnections.length
      : null,
  };
}

function getMemberId(member) {
  return (
    member?.id ||
    member?._id ||
    member?.memberId ||
    member?.member_id ||
    member?.memberstackId ||
    member?.memberstack_id ||
    member?.msMemberId ||
    member?.ms_member_id ||
    null
  );
}

function getPlanConnectionId(connection) {
  return connection?.planId || connection?.plan_id || null;
}

function getPriceId(connection) {
  return (
    connection?.payment?.priceId ||
    connection?.payment?.price_id ||
    connection?.priceId ||
    connection?.price_id ||
    null
  );
}

function getConnectionStatus(connection) {
  return normalizeStatus(
    connection?.status ||
      connection?.planStatus ||
      connection?.plan_status ||
      connection?.membershipStatus ||
      connection?.membership_status ||
      connection?.subscriptionStatus ||
      connection?.subscription_status ||
      connection?.stripeStatus ||
      connection?.stripe_status
  );
}

function getPlanConnections(member) {
  if (Array.isArray(member?.planConnections)) {
    return member.planConnections;
  }

  if (Array.isArray(member?.plan_connections)) {
    return member.plan_connections;
  }

  if (Array.isArray(member?.plans)) {
    return member.plans;
  }

  if (Array.isArray(member?.memberships)) {
    return member.memberships;
  }

  return [];
}

function getAllowedPlanFromMember(member) {
  const connections = getPlanConnections(member);

  if (connections.length === 0) {
    return null;
  }

  return connections.find(connection => {
    const status = getConnectionStatus(connection);
    const planId = getPlanConnectionId(connection);
    const priceId = getPriceId(connection);

    const isAllowedPlan =
      ALLOWED_PLAN_IDS.has(planId) || ALLOWED_PLAN_IDS.has(priceId);

    return isAllowedPlan && ALLOWED_STATUSES.has(status);
  });
}

function getAllowedIdFromConnection(connection) {
  const planId = getPlanConnectionId(connection);
  const priceId = getPriceId(connection);

  if (ALLOWED_PLAN_IDS.has(planId)) {
    return planId;
  }

  if (ALLOWED_PLAN_IDS.has(priceId)) {
    return priceId;
  }

  return null;
}

async function getMemberFromOldMemberstackGlobal() {
  await waitForMemberstackGlobals();

  if (typeof window === 'undefined') {
    return null;
  }

  // Older Memberstack/Webflow-style global
  if (window?.MemberStack?.onReady) {
    const response = await window.MemberStack.onReady;
    const member = unwrapMemberstackResponse(response);

    if (member) {
      console.log(
        '[AUTH] Using window.MemberStack.onReady member shape:',
        getDebugShape(member)
      );
      return member;
    }
  }

  // Memberstack DOM global injected on the Webflow site
  if (window?.$memberstackDom?.getCurrentMember) {
    const response = await window.$memberstackDom.getCurrentMember();
    const member = unwrapMemberstackResponse(response);

    if (member) {
      // console.log(
      //   '[AUTH] Using window.$memberstackDom.getCurrentMember shape:',
      //   getDebugShape(member)
      // );
      return member;
    }
  }

  // Some old/custom installs expose lowercase global
  if (window?.memberstack?.getCurrentMember) {
    const response = await window.memberstack.getCurrentMember();
    const member = unwrapMemberstackResponse(response);

    if (member) {
      console.log(
        '[AUTH] Using window.memberstack.getCurrentMember shape:',
        getDebugShape(member)
      );
      return member;
    }
  }

  return null;
}

async function getMemberFromPackage() {
  if (!memberstack?.getCurrentMember) {
    return null;
  }

  const response = await memberstack.getCurrentMember();
  const member = unwrapMemberstackResponse(response);

  if (member) {
    console.log(
      '[AUTH] Using imported memberstack client shape:',
      getDebugShape(member)
    );
  }

  return member;
}

async function buildMemberstackPayload() {
  let member = await getMemberFromOldMemberstackGlobal();

  if (!member) {
    member = await getMemberFromPackage();
  }

  if (!member) {
    throw new Error('No logged-in Memberstack member found.');
  }

  const memberId = getMemberId(member);

  if (!memberId) {
    console.log(
      '[AUTH] Memberstack object shape missing member id:',
      getDebugShape(member)
    );
    throw new Error('Logged-in Memberstack member is missing an id.');
  }

  const activePlan = getAllowedPlanFromMember(member);

  if (!activePlan) {
    console.log('[AUTH] No allowed active/trialing Memberstack plan found:', {
      memberShape: getDebugShape(member),
      planConnections: getPlanConnections(member),
    });

    throw new Error('No allowed active or trialing Memberstack plan found.');
  }

  const planId = getAllowedIdFromConnection(activePlan);
  const status = getConnectionStatus(activePlan);

  if (!planId || !status) {
    throw new Error('Memberstack active plan is missing planId or status.');
  }

  const payload = {
    type: 'AUTH',
    memberId,
    planId,
    status,
  };

  // console.log('[AUTH] Memberstack websocket auth payload:', {
  //   type: payload.type,
  //   memberId: payload.memberId,
  //   planId: payload.planId,
  //   status: payload.status,
  // });

  return payload;
}

export async function buildPremiumSellersAuthPayload() {
  if (AUTH_MODE === 'manual') {
    return validateLocalAuth();
  }

  return buildMemberstackPayload();
}

export async function buildMarketStructureAuthPayload() {
  return buildPremiumSellersAuthPayload();
}

export async function buildMeicAuthPayload() {
  const authPayload = await buildPremiumSellersAuthPayload();

  return {
    type: 'auth',
    memberId: authPayload.memberId,
  };
}

export function getAuthMode() {
  return AUTH_MODE;
}






//Old code that would not work in heroku.
// import memberstack from './memberstack_client';
//
// const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'memberstack';
//
// const LOCAL_AUTH = {
//   type: 'AUTH',
//   memberId: process.env.REACT_APP_LOCAL_MEMBER_ID,
//   planId: process.env.REACT_APP_LOCAL_PLAN_ID,
//   status: process.env.REACT_APP_LOCAL_PLAN_STATUS,
// };
//
// const ALLOWED_STATUSES = new Set(['active', 'trialing']);
//
// function validateLocalAuth() {
//   if (!LOCAL_AUTH.memberId || !LOCAL_AUTH.planId || !LOCAL_AUTH.status) {
//     throw new Error(
//       'Manual auth mode is enabled, but REACT_APP_LOCAL_MEMBER_ID, REACT_APP_LOCAL_PLAN_ID, or REACT_APP_LOCAL_PLAN_STATUS is missing.'
//     );
//   }
//
//   return LOCAL_AUTH;
// }
//
// function normalizeStatus(value) {
//   return String(value || '').trim().toLowerCase();
// }
//
// function sleep(ms) {
//   return new Promise(resolve => setTimeout(resolve, ms));
// }
//
// async function waitForMemberstackGlobals(maxWaitMs = 5000) {
//   const startedAt = Date.now();
//
//   while (Date.now() - startedAt < maxWaitMs) {
//     if (
//       typeof window !== 'undefined' &&
//       (window.MemberStack || window.$memberstackDom || window.memberstack)
//     ) {
//       return true;
//     }
//
//     await sleep(100);
//   }
//
//   return false;
// }
//
// function unwrapMemberstackResponse(response) {
//   if (!response) return null;
//
//   return (
//     response?.data?.member ||
//     response?.data?.currentMember ||
//     response?.data?.memberData ||
//     response?.data ||
//     response?.member ||
//     response?.currentMember ||
//     response?.memberData ||
//     response?.user ||
//     response
//   );
// }
//
// function getDirectValue(obj, keys) {
//   if (!obj || typeof obj !== 'object') return null;
//
//   for (const key of keys) {
//     if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
//       return obj[key];
//     }
//   }
//
//   return null;
// }
//
// function getNestedValue(obj, keys, maxDepth = 8) {
//   if (!obj || typeof obj !== 'object' || maxDepth < 0) return null;
//
//   const directValue = getDirectValue(obj, keys);
//   if (directValue !== null && directValue !== undefined) {
//     return directValue;
//   }
//
//   for (const value of Object.values(obj)) {
//     if (value && typeof value === 'object') {
//       const found = getNestedValue(value, keys, maxDepth - 1);
//       if (found !== null && found !== undefined) {
//         return found;
//       }
//     }
//   }
//
//   return null;
// }
//
// function getMemberId(memberLike) {
//   return getNestedValue(memberLike, [
//     'id',
//     '_id',
//     'memberId',
//     'member_id',
//     'memberstackId',
//     'memberstack_id',
//     'msMemberId',
//     'ms_member_id',
//   ]);
// }
//
// function getPlanId(planLike) {
//   return getNestedValue(planLike, [
//     'planId',
//     'plan_id',
//     'planID',
//     'priceId',
//     'price_id',
//     'membershipId',
//     'membership_id',
//     'id',
//     '_id',
//   ]);
// }
//
// function getPlanStatus(planLike) {
//   return normalizeStatus(
//     getNestedValue(planLike, [
//       'status',
//       'planStatus',
//       'plan_status',
//       'membershipStatus',
//       'membership_status',
//       'subscriptionStatus',
//       'subscription_status',
//       'stripeStatus',
//       'stripe_status',
//     ])
//   );
// }
//
// function isLikelyPlanObject(value) {
//   if (!value || typeof value !== 'object') return false;
//
//   const planId = getPlanId(value);
//   const status = getPlanStatus(value);
//
//   return Boolean(planId || status);
// }
//
// function flattenPlanCandidates(memberLike) {
//   const candidates = [];
//   const seen = new Set();
//
//   function walk(value, depth = 0) {
//     if (!value || depth > 8) return;
//
//     if (Array.isArray(value)) {
//       value.forEach(item => walk(item, depth + 1));
//       return;
//     }
//
//     if (typeof value !== 'object') return;
//
//     if (seen.has(value)) return;
//     seen.add(value);
//
//     if (isLikelyPlanObject(value)) {
//       candidates.push(value);
//     }
//
//     Object.values(value).forEach(item => walk(item, depth + 1));
//   }
//
//   walk(memberLike);
//
//   return candidates;
// }
//
// function findActiveOrTrialingPlan(memberLike) {
//   const candidates = flattenPlanCandidates(memberLike);
//
//   return candidates.find(plan => {
//     const planId = getPlanId(plan);
//     const status = getPlanStatus(plan);
//
//     return planId && ALLOWED_STATUSES.has(status);
//   });
// }
//
// function getDebugShape(value) {
//   if (!value || typeof value !== 'object') return value;
//
//   return {
//     topLevelKeys: Object.keys(value),
//     dataKeys: value?.data && typeof value.data === 'object'
//       ? Object.keys(value.data)
//       : null,
//     memberKeys: value?.member && typeof value.member === 'object'
//       ? Object.keys(value.member)
//       : null,
//     currentMemberKeys: value?.currentMember && typeof value.currentMember === 'object'
//       ? Object.keys(value.currentMember)
//       : null,
//   };
// }
//
// async function getMemberFromOldMemberstackGlobal() {
//   await waitForMemberstackGlobals();
//
//   if (typeof window === 'undefined') {
//     return null;
//   }
//
//   // Older Memberstack/Webflow-style global
//   if (window?.MemberStack?.onReady) {
//     const response = await window.MemberStack.onReady;
//     const member = unwrapMemberstackResponse(response);
//
//     if (member) {
//       console.log('[AUTH] Using window.MemberStack.onReady member shape:', getDebugShape(response));
//       return member;
//     }
//   }
//
//   // Memberstack DOM global injected on the site
//   if (window?.$memberstackDom?.getCurrentMember) {
//     const response = await window.$memberstackDom.getCurrentMember();
//     const member = unwrapMemberstackResponse(response);
//
//     if (member) {
//       console.log('[AUTH] Using window.$memberstackDom.getCurrentMember shape:', getDebugShape(response));
//       return member;
//     }
//   }
//
//   // Some old/custom installs expose lowercase global
//   if (window?.memberstack?.getCurrentMember) {
//     const response = await window.memberstack.getCurrentMember();
//     const member = unwrapMemberstackResponse(response);
//
//     if (member) {
//       console.log('[AUTH] Using window.memberstack.getCurrentMember shape:', getDebugShape(response));
//       return member;
//     }
//   }
//
//   return null;
// }
//
// async function getMemberFromPackage() {
//   if (!memberstack?.getCurrentMember) {
//     return null;
//   }
//
//   const response = await memberstack.getCurrentMember();
//   const member = unwrapMemberstackResponse(response);
//
//   if (member) {
//     console.log('[AUTH] Using imported memberstack client shape:', getDebugShape(response));
//   }
//
//   return member;
// }
//
// async function buildMemberstackPayload() {
//   let member = await getMemberFromOldMemberstackGlobal();
//
//   if (!member) {
//     member = await getMemberFromPackage();
//   }
//
//   if (!member) {
//     throw new Error('No logged-in Memberstack member found.');
//   }
//
//   const memberId = getMemberId(member);
//
//   if (!memberId) {
//     console.log('[AUTH] Memberstack object shape missing member id:', getDebugShape(member));
//     throw new Error('Logged-in Memberstack member is missing an id.');
//   }
//
//   const activePlan = findActiveOrTrialingPlan(member);
//
//   if (!activePlan) {
//     console.log('[AUTH] Memberstack object shape missing readable active/trialing plan:', getDebugShape(member));
//     throw new Error('No active or trialing Memberstack plan found.');
//   }
//
//   const planId = getPlanId(activePlan);
//   const status = getPlanStatus(activePlan);
//
//   if (!planId || !status) {
//     throw new Error('Memberstack active plan is missing planId or status.');
//   }
//
//   return {
//     type: 'AUTH',
//     memberId,
//     planId,
//     status,
//   };
// }
//
// export async function buildPremiumSellersAuthPayload() {
//   if (AUTH_MODE === 'manual') {
//     return validateLocalAuth();
//   }
//
//   return buildMemberstackPayload();
// }
//
// export async function buildMarketStructureAuthPayload() {
//   return buildPremiumSellersAuthPayload();
// }
//
// export async function buildMeicAuthPayload() {
//   const authPayload = await buildPremiumSellersAuthPayload();
//
//   return {
//     type: 'auth',
//     memberId: authPayload.memberId,
//   };
// }
//
// export function getAuthMode() {
//   return AUTH_MODE;
// }